import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  Chip,
  Box,
  Typography
} from '@mui/material';

const ProductSelector = ({ 
  clientId, 
  products, 
  selectedProductId, 
  onProductChange,
  onClientProductChange,
  size = "medium",     
  color = "primary",
  predictionEnabledOnly = false  // 🚀 残量更新時のみ true
}) => {
  const [clientProducts, setClientProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // 製品データの安全性チェック
  const safeProducts = React.useMemo(() => {
    if (!Array.isArray(products)) return [];
    
    return products.filter(product => 
      product && 
      product.id && 
      product.name && 
      product.active !== false
    );
  }, [products]);

  // 🚀 修正：残量更新時は既存の機能有効製品のみ、供給登録時は全製品取得
  useEffect(() => {
    const fetchClientProducts = async () => {
      if (!clientId) {
        setClientProducts([]);
        return;
      }

      try {
        setLoading(true);
        const token = sessionStorage.getItem("token");
        
        // 🚀 修正：残量更新時は予測機能有効な既存製品のみ取得
        const endpoint = predictionEnabledOnly 
          ? `/api/client-products/prediction-enabled?clientId=${clientId}`
          : `/api/client-products?clientId=${clientId}`;
        
        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const rawClientProducts = res.data || [];
        setClientProducts(rawClientProducts);
        
      } catch (err) {

        setClientProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClientProducts();
  }, [clientId, predictionEnabledOnly]);

  // 製品選択時の処理
  const handleProductSelection = async (productId) => {
    if (!productId || !clientId) {
      onProductChange(productId);
      return;
    }

    const selectedProduct = safeProducts.find(p => p.id === parseInt(productId, 10));
    if (!selectedProduct) {
      return;
    }

    setLoading(true);
    
    try {
      // 既存の顧客製品を確認
      const existingClientProduct = clientProducts.find(cp => cp.productId === parseInt(productId, 10));
      
      if (existingClientProduct) {
        // 🚀 修正：残量更新時は機能チェックを実行（既存のみ）
        if (predictionEnabledOnly && 
            (!existingClientProduct.predictionEnabled || !existingClientProduct.tankInstalled)) {
          alert("この製品は予測機能またはタンク設置が無効のため残量更新できません。");
          onProductChange("");
          if (onClientProductChange) {
            onClientProductChange("");
          }
          return;
        }
        
        // 既存の関連付けがある場合はそのまま使用
        onProductChange(productId);
        if (onClientProductChange) {
          onClientProductChange(existingClientProduct.id);
        }
      } else {
        // 🚀 修正：残量更新時は新規作成を行わない
        if (predictionEnabledOnly) {
          alert("残量更新は既存の取引関係がある製品でのみ可能です。");
          onProductChange("");
          if (onClientProductChange) {
            onClientProductChange("");
          }
          return;
        }
        
        // 🚀 供給登録時のみ新規関連付けを作成
        const token = sessionStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const createPayload = {
          clientId: clientId,
          productId: parseInt(productId, 10)
          // 供給登録時はデフォルト値のまま（機能OFF）
        };
        
        const response = await axios.post('/api/client-products', createPayload, { headers });
        
        if (!response.data || !response.data.id) {
          throw new Error('製品設定の作成に失敗しました');
        }
        
        // 新しい顧客製品をリストに追加
        setClientProducts(prev => [...prev, response.data]);
        
        // 選択状態を更新
        onProductChange(productId);
        if (onClientProductChange) {
          onClientProductChange(response.data.id);
        }
      }
    } catch (err) {
      alert("製品選択中にエラーが発生しました。");
      // エラーが発生した場合は選択をクリア
      onProductChange("");
      if (onClientProductChange) {
        onClientProductChange("");
      }
    } finally {
      setLoading(false);
    }
  };

  // 製品の状態を判定するヘルパー
  const getProductStatus = (product) => {
    const clientProduct = clientProducts.find(cp => cp.productId === product.id);
    
    if (!clientProduct) {
      return { 
        type: 'new', 
        label: '新規', 
        color: 'primary' 
      };
    } else if (predictionEnabledOnly && 
               (!clientProduct.predictionEnabled || !clientProduct.tankInstalled)) {
      return { 
        type: 'disabled', 
        label: '機能無効', 
        color: 'error' 
      };
    } else if (clientProduct.totalCapacity == null) {
      return { 
        type: 'unconfigured', 
        label: '要設定', 
        color: 'warning' 
      };
    } else {
      return { 
        type: 'configured', 
        label: '設定済み', 
        color: 'success' 
      };
    }
  };

  // 🚀 修正：残量更新時は既存の機能有効製品のみ表示
  const displayProducts = React.useMemo(() => {
    if (!predictionEnabledOnly) {
      // 供給登録時は全製品を表示
      return safeProducts;
    }
    
    // 🚀 残量更新時は既存の中間テーブルがあり、かつ機能が有効な製品のみ表示
    return safeProducts.filter(product => {
      const clientProduct = clientProducts.find(cp => cp.productId === product.id);
      
      // 中間テーブルが存在しない場合は表示しない
      if (!clientProduct) {
        return false;
      }
      
      // 既存製品は両方の機能が有効な場合のみ表示
      return clientProduct.predictionEnabled === true && clientProduct.tankInstalled === true;
    });
  }, [safeProducts, clientProducts, predictionEnabledOnly]);

  // クライアントIDがない場合
  if (!clientId) {
    return (
      <FormControl fullWidth sx={{ mb: 2 }} disabled size={size}>
        <InputLabel id="product-select-label">製品</InputLabel>
        <Select
          labelId="product-select-label"
          value=""
          label="製品"
          disabled
        >
          <MenuItem value="">
            <em>顧客を先に選択してください</em>
          </MenuItem>
        </Select>
        <FormHelperText>顧客を選択すると製品が表示されます</FormHelperText>
      </FormControl>
    );
  }

  // 有効な製品が存在しない場合
  if (displayProducts.length === 0) {
    return (
      <FormControl fullWidth sx={{ mb: 2 }} disabled size={size}>
        <InputLabel id="product-select-label">製品</InputLabel>
        <Select
          labelId="product-select-label"
          value=""
          label="製品"
          disabled
        >
          <MenuItem value="">
            <em>
              {predictionEnabledOnly 
                ? '残量更新可能な製品がありません' 
                : '利用可能な製品がありません'
              }
            </em>
          </MenuItem>
        </Select>
        <FormHelperText>
          {predictionEnabledOnly 
            ? 'この顧客には残量更新可能な製品設定がありません。先に供給登録を行い、製品設定で予測機能を有効にしてください。' 
            : '製品を登録してください'
          }
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth sx={{ mb: 2 }} size={size}>
      <InputLabel id="product-select-label">
        製品{predictionEnabledOnly && ' (残量更新可能のみ)'}
      </InputLabel>
      <Select
        labelId="product-select-label"
        value={selectedProductId || ''}
        onChange={(e) => handleProductSelection(e.target.value)}
        label={`製品${predictionEnabledOnly ? ' (残量更新可能のみ)' : ''}`}
        disabled={loading}
      >
        <MenuItem value="">
          <em>製品を選択してください</em>
        </MenuItem>
        {displayProducts.map((product) => {
          const status = getProductStatus(product);
          
          return (
            <MenuItem key={product.id} value={product.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography>{product.name}</Typography>
                <Chip
                  label={status.label}
                  size="small"
                  color={status.color}
                  variant={status.type === 'new' ? 'outlined' : 'filled'}
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              </Box>
            </MenuItem>
          );
        })}
      </Select>
      {loading && (
        <FormHelperText>製品情報を処理中...</FormHelperText>
      )}
      {predictionEnabledOnly && (
        <FormHelperText>
          残量更新は既存の取引関係があり、予測機能とタンク設置が有効な製品でのみ可能です
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default ProductSelector;